/**
 * `useUIContext` exposes the resolved theme state for components that need
 * to make rendering decisions outside of CSS variables (e.g. choosing an
 * icon color, deciding whether to render a sub-feature).
 *
 * Most components should rely on `--rr-*` CSS variables instead and only
 * read this context for branching logic.
 */

import { createContext, useContext } from 'react';
import type { Density, TextScale, ReducedMotion, ThemeMode } from '@repletoryreach/sdk-utils/store';

export interface UIContextValue {
  /** Resolved color scheme: never 'system', always 'light' | 'dark'. */
  resolvedColorScheme: 'light' | 'dark';
  /** Raw mode set on the store (may be 'system'). */
  colorScheme: ThemeMode;
  primary: string;
  secondary: string;
  tertiary: string;
  textScale: TextScale;
  density: Density;
  reducedMotion: ReducedMotion;
  reducedMotionResolved: boolean;
  highContrast: boolean;
  /** Selector for the element CSS vars are scoped to (default `':root'`). */
  cssVarsSelector: string;
}

export const UIContext = createContext<UIContextValue | null>(null);

export function useUIContext(): UIContextValue {
  const ctx = useContext(UIContext);
  if (!ctx) {
    // Fail soft: components outside UIProvider still work with sensible defaults.
    return {
      resolvedColorScheme: 'light',
      colorScheme: 'system',
      primary: '#0d9488',
      secondary: '#6366f1',
      tertiary: '#f59e0b',
      textScale: 'md',
      density: 'comfortable',
      reducedMotion: 'auto',
      reducedMotionResolved: false,
      highContrast: false,
      cssVarsSelector: ':root'
    };
  }
  return ctx;
}

/** Quick helper; read `data-theme` resolved from the context. */
export function useResolvedColorScheme(): 'light' | 'dark' {
  return useUIContext().resolvedColorScheme;
}
